import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import MenuIcon from "@mui/icons-material/Menu";
import {
  Box,
  Checkbox,
  CSSObject,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  styled,
  Theme,
  Tooltip,
  useMediaQuery,
  useTheme,
  alpha,
} from "@mui/material";
import React, { useContext, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import IconComponent from "../../common/components/IconComponent";
import { usePersistedSetting } from "../../common/hooks/usePersistedSetting";
import { MenuItemDesc, MenuSectionDesc } from "../../containers/Navigation";
import BundleContext from "../../UserInterfaceModule/Contexts/BundleContext";
import useLocationHistory from "../../UserInterfaceModule/Hooks/useLocationHistory";
import AuthContext from "../AuthContext";
import MenuListItem from "./MenuListItem";
import MenuSection from "./MenuSection";
import DrawerLogo from "./DrawerLogo";
import MenuListItemAvatar from "./MenuListItemAvatar";

const DRAWER_WIDTH = 240;
const LEFT_MENU_OPEN_STATE_KEY = "leftMenu.open";
const LEFT_MENU_HOVER_ENABLED_KEY = "leftMenu.hoverEnabled";

function normalizePath(path: string) {
  const normalized = path.split(/[?#]/)[0].replace(/\/+$/, "");
  return normalized || "/";
}

function isRouteSelected(currentPath: string, routePath?: string) {
  if (!routePath) {
    return false;
  }

  const current = normalizePath(currentPath);
  const route = normalizePath(routePath);

  if (route === "/") {
    return current === "/";
  }

  return current === route || current.startsWith(`${route}/`);
}

const openedMixin = (theme: Theme): CSSObject => ({
  width: DRAWER_WIDTH,
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  overflowX: "hidden",
});

const closedMixin = (theme: Theme): CSSObject => ({
  transition: theme.transitions.create("width", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  overflowX: "hidden",
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up("sm")]: {
    width: `calc(${theme.spacing(8)} + 1px)`,
  },
});

const DrawerHeader = styled("div")(({ theme }) => ({
  display: "flex",
  flexShrink: 0,
  alignItems: "center",
  justifyContent: "flex-end",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
}));

const StyledDrawer = styled(Drawer, {
  shouldForwardProp: (prop) => prop !== "open" && prop !== "isOverlay",
})<{ open?: boolean; isOverlay?: boolean }>(({ theme }) => ({
  width: DRAWER_WIDTH,
  flexShrink: 0,
  whiteSpace: "nowrap",
  boxSizing: "border-box",
  "& .MuiDrawer-paper": {
    overflowY: "hidden",
    boxShadow: theme.shadows[3],
    backgroundColor: theme.palette.mode === "light" ? theme.palette.background.paper : "#3d3935",
    borderRight: theme.palette.mode === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : undefined,
  },
  variants: [
    {
      props: ({ open, isOverlay }) => open && !isOverlay,
      style: {
        ...openedMixin(theme),
        "& .MuiDrawer-paper": openedMixin(theme),
      },
    },
    {
      props: ({ open, isOverlay }) => !open || isOverlay,
      style: {
        ...closedMixin(theme),
        "& .MuiDrawer-paper": closedMixin(theme),
      },
    },
    {
      props: ({ isOverlay }) => isOverlay,
      style: {
        "& .MuiDrawer-paper": {
          position: "fixed",
          zIndex: theme.zIndex.drawer + 1,
          width: DRAWER_WIDTH,
        },
      },
    },
  ],
}));

const DrawerNavigation = styled("nav")(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  overflowX: "hidden",
  overflowY: "auto",
  "@media (forced-colors: none)": {
    scrollbarWidth: "thin",
    scrollbarColor: `${alpha(theme.palette.text.primary, 0.5)} transparent`,
    "@supports selector(::-webkit-scrollbar)": {
      scrollbarWidth: "auto",
      scrollbarColor: "auto",
      "&::-webkit-scrollbar": { width: 10, height: 10 },
      "&::-webkit-scrollbar-track": { backgroundColor: "transparent" },
      "&::-webkit-scrollbar-thumb": {
        backgroundColor: alpha(theme.palette.text.primary, 0.5),
        backgroundClip: "padding-box",
        border: "2px solid transparent",
        borderRadius: 10,
      },
      "&::-webkit-scrollbar-thumb:hover": { backgroundColor: alpha(theme.palette.text.primary, 0.7) },
      "&::-webkit-scrollbar-thumb:active": { backgroundColor: theme.palette.text.secondary },
      "&::-webkit-scrollbar-corner": { backgroundColor: "transparent" },
      "&::-webkit-scrollbar-button": { display: "none", width: 0, height: 0 },
    },
  },
}));

interface LeftMenuProps {
  onToggle?: () => void;
}

export default function LeftMenu({ onToggle }: LeftMenuProps) {
  const [open, setOpen] = usePersistedSetting<boolean>(LEFT_MENU_OPEN_STATE_KEY, false);
  const [hoverEnabled, setHoverEnabled] = usePersistedSetting<boolean>(LEFT_MENU_HOVER_ENABLED_KEY, true);
  const [isHovering, setIsHovering] = React.useState(false);
  const [isMobileOpen, setIsMobileOpen] = React.useState(false);
  const auth = useContext(AuthContext);
  const bundle = useContext(BundleContext);
  const { navigate } = useLocationHistory();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const isDrawerOpen = isMobile ? isMobileOpen : open || (hoverEnabled && isHovering);
  const isOverlayMode = !isMobile && !open && hoverEnabled && isHovering;

  useEffect(() => {
    setIsMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!isMobile) {
      setIsMobileOpen(false);
    }
  }, [isMobile]);

  const handleMouseEnter = () => {
    if (!isMobile && hoverEnabled) {
      setIsHovering(true);
    }
  };

  const handleMouseLeave = () => {
    setIsHovering(false);
  };

  const handleHoverToggle = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHoverEnabled(event.target.checked);
  };

  const dynamicMenuItems = useMemo<MenuItemDesc[]>(() => {
    const items: MenuItemDesc[] = bundle.Views.filter((view) => view.Menu && view.Menu.Visible !== false).map((view) => ({
      title: view.Menu!.Title,
      description: view.Menu?.Description,
      sectionTitle: view.Menu?.SectionTitle || "Main",
      sectionOrder: view.Menu?.SectionOrder ?? 0,
      order: view.Menu?.Order ?? 0,
      icon: <IconComponent iconName={view.Menu?.IconName} />,
      path: view.RoutePath,
      requiredAuth: view.RequiresAuthentication,
    }));
    return items;
  }, [bundle.Views]);

  const menuItems: MenuSectionDesc[] = useMemo(() => {
    const menuItemsBySection = dynamicMenuItems.reduce<Record<string, MenuItemDesc[]>>((acc, item) => {
      const sectionTitle = item.sectionTitle;
      if (!acc[sectionTitle]) {
        acc[sectionTitle] = [];
      }
      acc[sectionTitle].push(item);
      return acc;
    }, {});
    const sections: MenuSectionDesc[] = Object.entries(menuItemsBySection).map(([title, items]) => {
      // Sort items within the section by order asc, then by title asc
      const sortedItems = [...items].sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.title.localeCompare(b.title));
      // Section order = minimum SectionOrder among its items
      const sectionOrder = Math.min(...items.map((i) => i.sectionOrder ?? 0));
      return { title, sectionOrder, items: sortedItems };
    });
    // Sort sections by sectionOrder asc, then by title asc
    sections.sort((a, b) => a.sectionOrder - b.sectionOrder || (a.title ?? "").localeCompare(b.title ?? ""));
    return sections;
  }, [dynamicMenuItems]);

  const handleDrawerToggle = () => {
    if (isMobile) {
      setIsMobileOpen((value) => !value);
    } else {
      setOpen(!open);
    }
    if (onToggle) {
      onToggle();
    }
  };

  const handleOnClick = (item: MenuItemDesc) => {
    if (item.path) {
      navigate(item.path, item.title, true);
    }
    if (isMobile) {
      setIsMobileOpen(false);
    }
  };

  const sections = useMemo<MenuSectionDesc[]>(
    () =>
      menuItems
        .map((section) => {
          const items = section.items.filter((item) => item.requiredAuth === false || item.requiredAuth === auth.isAuthenticated);
          return {
            ...section,
            items: items,
          };
        })
        .filter((section) => section.items.length > 0),
    [auth.isAuthenticated, menuItems]
  );

  const menuContent = (menuOpen: boolean, showHoverToggle: boolean) => (
    <>
      <DrawerHeader>
        <List sx={{ width: "100%", p: 0 }}>
          {!menuOpen ? (
            <MenuListItem open={menuOpen} icon={<MenuIcon />} primaryText="Open Menu" onClick={handleDrawerToggle} showText={false} />
          ) : (
            <MenuListItem open={menuOpen} rightIcon={<ChevronLeftIcon />} primaryText="Close Menu" onClick={handleDrawerToggle} showText={false} />
          )}
        </List>
      </DrawerHeader>

      <DrawerLogo bundle={bundle} open={menuOpen} />

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flexGrow: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <DrawerNavigation aria-label="Main navigation" tabIndex={0}>
          {sections.map((section) => (
            <MenuSection key={`${bundle.Id}:${section.title}`} appId={bundle.Id} title={section.title} open={menuOpen}>
                {section.items.map((item, index) => (
                  <MenuListItem
                    key={index}
                    open={menuOpen}
                    icon={item.icon}
                    primaryText={item.title}
                    secondaryText={item.description}
                    onClick={() => handleOnClick(item)}
                    href={item.path}
                    selected={isRouteSelected(location.pathname, item.path)}
                  />
                ))}
            </MenuSection>
          ))}
        </DrawerNavigation>

        <Box component="footer" sx={{ flexShrink: 0 }}>
          {!menuOpen && <Divider />}
          <List>
            {showHoverToggle && !open && (
              <ListItem sx={{ justifyContent: "center", py: 0.5 }}>
                <Tooltip title="Enable hover to open" placement="right">
                  <Checkbox checked={hoverEnabled} onChange={handleHoverToggle} size="small" sx={{ p: 0.5 }} />
                </Tooltip>
              </ListItem>
            )}
            <MenuListItemAvatar open={menuOpen} additionalSx={{ pb: 1 }} />
          </List>
        </Box>
      </Box>
    </>
  );

  if (isMobile) {
    return (
      <>
        {!isMobileOpen && (
          <IconButton
            aria-label="Open navigation menu"
            onClick={handleDrawerToggle}
            sx={{ position: "fixed", top: 8, left: 8, zIndex: (currentTheme) => currentTheme.zIndex.appBar, backgroundColor: "background.paper", boxShadow: 1 }}
          >
            <MenuIcon />
          </IconButton>
        )}
        <Drawer
          variant="temporary"
          open={isMobileOpen}
          onClose={() => setIsMobileOpen(false)}
          ModalProps={{ keepMounted: true }}
          PaperProps={{
            sx: {
              width: DRAWER_WIDTH,
              maxWidth: "85vw",
              overflowY: "hidden",
              backgroundColor: (currentTheme) => (currentTheme.palette.mode === "light" ? currentTheme.palette.background.paper : "#3d3935"),
            },
          }}
        >
          {menuContent(true, false)}
        </Drawer>
      </>
    );
  }

  return (
    <StyledDrawer variant="permanent" open={isDrawerOpen} isOverlay={isOverlayMode} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
      {menuContent(isDrawerOpen, true)}
    </StyledDrawer>
  );
}
